import {Injectable} from '@angular/core';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';

@Injectable({providedIn: 'root'})
export class VideoEmbedService {
  constructor(private sanitizer: DomSanitizer) {
  }

  /**
   * Accepts either:
   * - a raw YouTube video id (e.g. "dQw4w9WgXcQ")
   * - or a full/short YouTube URL
   *
   * Returns a SafeResourceUrl for <iframe [src]> or null if input is empty/invalid.
   */
  toYouTubeEmbedSrc(input: string | null | undefined): SafeResourceUrl | null {
    const trimmed = String(input ?? '').trim();
    if (!trimmed) return null;

    const videoId = this.extractYouTubeId(trimmed);
    if (!videoId) return null;

    const url = `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&playsinline=1`;
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  private extractYouTubeId(input: string): string | null {
    const inputWithoutQuery = input.split('?')[0];
    // input is video id
    if (/^[a-zA-Z0-9_-]{8,20}$/.test(inputWithoutQuery) && !inputWithoutQuery.includes('/')) {
      return inputWithoutQuery;
    }

    // input is short URL
    const partialWatch = input.match(/(?:^|\/)watch\?v=([a-zA-Z0-9_-]{8,20})(?:&|$)/);
    if (partialWatch && partialWatch[1]) {
      const v = partialWatch[1];
      if (v && /^[a-zA-Z0-9_-]{8,20}$/.test(v)) {
        console.log(`Extracted YouTube video ID from partial watch URL: ${v}`);
        return v;
      }
    }

    // input is URL
    try {
      const url = new URL(input);

      if (url.hostname.includes('youtu.be')) {
        const id = url.pathname.split('/').filter(Boolean)[0];
        return id && /^[a-zA-Z0-9_-]{8,20}$/.test(id) ? id : null;
      }

      if (url.hostname.includes('youtube.com')) {
        const v = url.searchParams.get('v');
        if (v && /^[a-zA-Z0-9_-]{8,20}$/.test(v)) return v;

        const parts = url.pathname.split('/').filter(Boolean);
        const embedIndex = parts.indexOf('embed');
        if (embedIndex !== -1 && parts[embedIndex + 1]) {
          return parts[embedIndex + 1];
        }
      }
    } catch (e) {
      // Not a URL; fall through.
    }

    return null;
  }
}
